import { ActivityFilterDateRange } from '@/types/activityDateRange';
import { CallingStatusFilterDateRange } from '@/types/callingstatusDateRange';
import {
  MailingStatus,
  ParticipantStatus,
  RosterCriteria,
  RosterTypes,
  SupporterStatus,
  TaskChoice,
  TaskKind,
} from './filterEnumTypes';
import { NotRenewingType } from '@/types/shared';
import type { TemplatePlaceholder } from './templatePlaceholder';

export type SearchFilter =
  | AllFilter
  | ContactIdsFilter
  | GroupIdsFilter
  | TagIdsFilter
  | GeometryIdsFilter
  | ListIdsFilter
  | TestOnlyContactIdsRangeFilter
  | NoneFilter
  | AgeFilter
  | HasPhoneFilter
  | HasEmailFilter
  | SupporterStatusesFilter
  | PathwayStepsFilter
  | MembershipFilter
  | MailingFilter
  | TaskFilter
  | RosterFilter
  | EventAttendanceFilter
  | EngagementHistoryFilter
  | CallingStatusFilter
  | CommsPreferencesFilter
  | StatisticalArea1Filter;

export type SearchFilterWithValue = Exclude<
  SearchFilter,
  TestOnlyContactIdsRangeFilter | AllFilter | NoneFilter
>;

type CommonFilterProps = {
  name: string;
};

export type TestOnlyContactIdsRangeFilter = CommonFilterProps & {
  name: 'testOnlyContactIdsRange';
  range: [number, number];
};

export type NoneFilter = CommonFilterProps & {
  name: 'none';
};

export type AllFilter = CommonFilterProps & {
  name: 'all';
};

export type ContactIdsFilter = CommonFilterProps & {
  name: 'contactIds';
  value: number[];
};

export type GroupIdsFilter = CommonFilterProps & {
  name: 'groupIds';
  value: number[] | TemplatePlaceholder;
};

export type TagIdsFilter = CommonFilterProps & {
  name: 'tagIds';
  value: number[] | TemplatePlaceholder;
};

export type GeometryIdsFilter = CommonFilterProps & {
  name: 'geometryIds';
  value: number[] | TemplatePlaceholder;
  operator?: 'lives in' | 'lives or placement in' | 'placement in';
};

export type ListIdsFilter = CommonFilterProps & {
  name: 'listIds';
  value: number[] | TemplatePlaceholder;
};

export type AgeFilter = CommonFilterProps & {
  name: 'age';
  value: number | TemplatePlaceholder;
  range?: 'Equal to' | 'Older than or equal to' | 'Younger than or equal to';
};

export type HasPhoneFilter = CommonFilterProps & {
  name: 'hasPhone';
  value: 'any' | 'mobile' | 'landline' | TemplatePlaceholder;
};

export type HasEmailFilter = CommonFilterProps & {
  name: 'hasEmail';
  value: boolean | TemplatePlaceholder;
};

export type SupporterStatusesFilter = CommonFilterProps & {
  name: 'supporterStatuses';
  value: SupporterStatus[] | TemplatePlaceholder;
};

export type StatisticalArea1Filter = CommonFilterProps & {
  name: 'sa1';
  value: number[] | TemplatePlaceholder;
};

export type PathwayStepsFilter = CommonFilterProps & {
  name: 'pathwaySteps';
  value: number[] | TemplatePlaceholder;
};

/** A non-compound search filter is a simple one where the value is not an object containing fields, and is just a single number, string or array*/
export type NonCompoundSearchFilter =
  | TagIdsFilter
  | GeometryIdsFilter
  | ListIdsFilter
  | AgeFilter
  | HasPhoneFilter
  | HasEmailFilter
  | SupporterStatusesFilter
  | StatisticalArea1Filter
  | PathwayStepsFilter;

export function isNonCompoundSearchFilter(filter: SearchFilter): filter is NonCompoundSearchFilter {
  return [
    'groupIds',
    'tagIds',
    'geometryIds',
    'listIds',
    'age',
    'hasPhone',
    'hasEmail',
    'supporterStatuses',
    'pathwaySteps',
    'sa1',
  ].includes(filter.name);
}

export type MembershipFilter = CommonFilterProps & {
  name: 'membership';
  value: {
    statusIds: number[] | TemplatePlaceholder;
    branchIds: number[] | TemplatePlaceholder;
    notRenewing: NotRenewingType | TemplatePlaceholder;
  };
};

export type MailingFilter = CommonFilterProps & {
  name: 'mailing';
  value: {
    mailingIds: number[] | TemplatePlaceholder;
    mailingStatus: MailingStatus | TemplatePlaceholder;
  };
};

export type TaskFilter = CommonFilterProps & {
  name: 'task';
  value: {
    electionIds: number[] | TemplatePlaceholder;
    isSinceSpecificElection: boolean;
    kinds: TaskKind[] | TemplatePlaceholder;
    choices: TaskChoice[] | TemplatePlaceholder;
    note?: string | TemplatePlaceholder;
  };
};

export function isTaskFilter(value: SearchFilter): value is TaskFilter {
  return value.name === 'task';
}

export type RosterFilter = CommonFilterProps & {
  name: 'roster';
  value: {
    electionId: number | TemplatePlaceholder;
    electorateIds: number[] | TemplatePlaceholder;
    rosterTypes: RosterTypes[] | TemplatePlaceholder;
    votingPeriod: RosterCriteria['votingPeriod'] | TemplatePlaceholder;
    shiftStatus: RosterCriteria['shiftStatus'] | TemplatePlaceholder;
  };
};

export type EventAttendanceFilter = CommonFilterProps & {
  name: 'eventAttendance';
  value: {
    statusIds: ParticipantStatus[] | TemplatePlaceholder;
    eventTypeIds: number[] | TemplatePlaceholder;
    eventDateRange: {
      startDate?: string | TemplatePlaceholder;
      endDate?: string | TemplatePlaceholder;
    };
    eventIds: number[] | TemplatePlaceholder;
    eventCampaignIds: number[] | TemplatePlaceholder;
  };
};

export function isEventAttendanceFilter(value: SearchFilter): value is EventAttendanceFilter {
  return value.name === 'eventAttendance';
}

export type TemplatableEngagementFilterDateRangeWithTemplates = Omit<
  ActivityFilterDateRange,
  'displayedStartDate' | 'displayedEndDate' | 'periodDetails'
> & {
  displayedStartDate: ActivityFilterDateRange['displayedStartDate'] | TemplatePlaceholder;
  displayedEndDate: ActivityFilterDateRange['displayedEndDate'] | TemplatePlaceholder;
  periodDetails: ActivityFilterDateRange['periodDetails'] | TemplatePlaceholder;
};

export type EngagementHistoryFilter = CommonFilterProps & {
  name: 'engagementHistory';
  value: {
    activityIds: number[] | TemplatePlaceholder;
    activityStatusIds: number[] | TemplatePlaceholder;
    subject?: string | TemplatePlaceholder;
    activityDateRange: TemplatableEngagementFilterDateRangeWithTemplates;
  };
};

export function isEngagementHistoryFilter(value: SearchFilter): value is EngagementHistoryFilter {
  return value.name === 'engagementHistory';
}

export type TemplatableCallingStatusFilterDateRangeWithTemplates = Omit<
  CallingStatusFilterDateRange,
  'callingPeriodQuantity' | 'displayedStartDate' | 'displayedEndDate'
> & {
  callingPeriodQuantity:
    | CallingStatusFilterDateRange['callingPeriodQuantity']
    | TemplatePlaceholder;
  displayedStartDate: CallingStatusFilterDateRange['displayedStartDate'] | TemplatePlaceholder;
  displayedEndDate: CallingStatusFilterDateRange['displayedEndDate'] | TemplatePlaceholder;
};

export type CallingStatusFilter = CommonFilterProps & {
  name: 'callingStatus';
  value: {
    callingStatusLabels: string[] | TemplatePlaceholder;
    callingStatusInclusion: boolean; // Technically always true in advanced search
    callingStatusDateRange: TemplatableCallingStatusFilterDateRangeWithTemplates;
    callingStatusListIds: number[] | TemplatePlaceholder;
    callingStatusUserSubs: string[] | TemplatePlaceholder;
    callingStatusCampaignIds: number[] | TemplatePlaceholder;
  };
};

export function isCallingStatusFilter(value: SearchFilter): value is CallingStatusFilter {
  return value.name === 'callingStatus';
}

export type CommsPreferencesFilter = CommonFilterProps & {
  name: 'commsPreferences';
  value: {
    commsPreferencesLabels: string[] | TemplatePlaceholder;
    commsPreferencesInclusion: boolean; // Technically always true in advanced search
  };
};

